import { IComparer } from "./Comparers/IComparer";
import { Constructor, Nullable } from "./Extensions";
import { ArrayHelper } from "./Helpers/ArrayHelper";
import { NAMESPACE } from "./_Namespace";

export class LinqArray<T> extends Array<T>
{
    // #region Static

    // #region Class Identity

    public static classNamespace = NAMESPACE + "LinqArray";
    public static className = "LinqArray";
    public static get classFullName() { return `${this.classNamespace}.${this.className}`; }

    // #endregion

    // #region Public Methods

    public static create<T>(array?: Nullable<T[]>): LinqArray<T>
    {
        const
            linqArray = Object.create(LinqArray.prototype) as LinqArray<T>;

        if (array)
            linqArray.push(...array);

        return linqArray;
    }

    // #endregion

    // #endregion

    // #region Instance

    // #region Constructor

    protected constructor(...items: T[])
    {
        super(...items);
    }

    // #endregion

    // #region Public Methods

    public add(item: T): LinqArray<T>
    {
        ArrayHelper.add(this, item);
        return this;
    }
    public addRange(range: T[]): LinqArray<T>
    {
        ArrayHelper.addRange(this, range);
        return this;
    }
    public all(predicate?: (item: T, index: number, array: T[]) => boolean): boolean
    {
        return ArrayHelper.all(this, predicate);
    }
    public any(predicate?: (item: T, index: number, array: T[]) => boolean): boolean
    {
        return ArrayHelper.any(this, predicate);
    }
    public clear(): LinqArray<T>
    {
        ArrayHelper.clear(this);
        return this;
    }
    public contains(item: T, comparer?: IComparer<T>): boolean
    {
        return ArrayHelper.contains(this, item, comparer);
    }
    public count(predicate?: (item: T, index: number, array: T[]) => boolean): number
    {
        return ArrayHelper.count(this, predicate);
    }
    public distinct(comparer?: IComparer<T>): LinqArray<T>
    {
        return LinqArray.create(ArrayHelper.distinct(this, comparer));
    }
    public each(callbackfn: (item: T, index: number, array: T[]) => boolean | undefined | void): LinqArray<T>
    {
        ArrayHelper.each(this, callbackfn);
        return this;
    }
    public first(predicate?: (item: T, index: number, array: T[]) => boolean): T
    {
        return ArrayHelper.first(this, predicate);
    }
    public firstOrDefault(predicate?: (item: T, index: number, array: T[]) => boolean): Nullable<T>
    {
        return ArrayHelper.firstOrDefault(this, predicate);
    }
    public groupBy<K, E, R>(keySelector: (item: T) => K, elementSelector: (item: T) => E, resultSelector: (key: K, elements: E[]) => R): LinqArray<R>
    {
        return LinqArray.create(ArrayHelper.groupBy(this, keySelector, elementSelector, resultSelector));
    }
    public indexOfWithComparer(item: T, comparer?: IComparer<T>): number
    {
        return ArrayHelper.indexOfWithComparer(this, item, comparer);
    }
    public last(predicate?: (item: T, index: number, array: T[]) => boolean): T
    {
        return ArrayHelper.last(this, predicate);
    }
    public lastOrDefault(predicate?: (item: T, index: number, array: T[]) => boolean): Nullable<T>
    {
        return ArrayHelper.lastOrDefault(this, predicate);
    }
    public max(filter?: (item: T, index: number, array: T[]) => boolean): Nullable<T>
    {
        return ArrayHelper.max(this, filter);
    }
    public min(filter?: (item: T, index: number, array: T[]) => boolean): Nullable<T>
    {
        return ArrayHelper.min(this, filter);
    }
    public ofType<U extends T>(type: Constructor<U>): LinqArray<U>
    {
        return LinqArray.create(ArrayHelper.ofType(this, type));
    }
    public orderBy(selector: (item: T) => any): OrderedLinqArray<T>
    {
        return OrderedLinqArray.createOrdered(this, [{ selector, descending: false }]);
    }
    public orderByDescending(selector: (item: T) => any): OrderedLinqArray<T>
    {
        return OrderedLinqArray.createOrdered(this, [{ selector, descending: true }]);
    }
    public remove(item: T): LinqArray<T>
    {
        ArrayHelper.remove(this, item);
        return this;
    }
    public removeWhere(selector: (item: T) => boolean): LinqArray<T>
    {
        ArrayHelper.removeWhere(this, selector);
        return this;
    }
    public select<U>(callbackfn: (item: T, index: number, array: T[]) => U): LinqArray<U>
    {
        return LinqArray.create(ArrayHelper.select(this, callbackfn));
    }
    public selectMany<U>(callbackfn: (item: T, index: number, array: T[]) => U[]): LinqArray<U>
    {
        return LinqArray.create(ArrayHelper.selectMany(this, callbackfn));
    }
    public sequenceEqualTo(arrayComparison: T[]): boolean
    {
        return ArrayHelper.sequenceEqualTo(this, arrayComparison);
    }
    public single(predicate?: (item: T, index: number, array: T[]) => boolean): T
    {
        return ArrayHelper.single(this, predicate);
    }
    public singleOrDefault(predicate?: (item: T, index: number, array: T[]) => boolean): Nullable<T>
    {
        return ArrayHelper.singleOrDefault(this, predicate);
    }
    public skip(number: number): LinqArray<T>
    {
        return LinqArray.create(ArrayHelper.skip(this, number));
    }
    public sum(filter?: (item: T, index: number, array: T[]) => boolean): number
    {
        return ArrayHelper.sum(this, filter);
    }
    public take(number: number): LinqArray<T>
    {
        return LinqArray.create(ArrayHelper.take(this, number));
    }
    public toArray()
    {
        return [...this];
    }
    public where(predicate: (item: T, index: number, array: T[]) => boolean): LinqArray<T>
    {
        return LinqArray.create(ArrayHelper.where(this, predicate));
    }

    // #endregion

    // #endregion
}

type SortEntry<T> = { selector: (item: T) => any; descending: boolean };

export class OrderedLinqArray<T> extends LinqArray<T>
{
    // #region Static

    // #region Class Identity

    public static classNamespace = NAMESPACE + "OrderedLinqArray";
    public static className = "OrderedLinqArray";
    public static get classFullName() { return `${this.classNamespace}.${this.className}`; }

    // #endregion

    // #region Internal Methods

    public static createOrdered<T>(array: T[], entries: SortEntry<T>[]): OrderedLinqArray<T>
    {
        const
            sorted = [...array].sort((a, b) =>
            {
                for (const entry of entries)
                {
                    const ka = entry.selector(a), kb = entry.selector(b);
                    if (ka < kb) return entry.descending ? 1 : -1;
                    if (ka > kb) return entry.descending ? -1 : 1;
                }
                return 0;
            }),
            result = Object.create(OrderedLinqArray.prototype) as OrderedLinqArray<T>;

        result.push(...sorted);
        result._sortEntries = entries;
        return result;
    }

    // #endregion

    // #endregion

    // #region Instance

    // #region Global Variables

    private _sortEntries!: SortEntry<T>[];

    // #endregion

    // #region Constructor

    protected constructor()
    {
        super();
    }

    // #endregion

    // #region Public Methods

    public thenBy(selector: (item: T) => any): OrderedLinqArray<T>
    {
        return OrderedLinqArray.createOrdered(this, [...this._sortEntries, { selector, descending: false }]);
    }
    public thenByDescending(selector: (item: T) => any): OrderedLinqArray<T>
    {
        return OrderedLinqArray.createOrdered(this, [...this._sortEntries, { selector, descending: true }]);
    }

    // #endregion

    // #endregion
}